import React, { useEffect, useState, useRef } from 'react';
import { TilePayload, TileResponse, clearSelection, removeTile, selectSelectedTiles, addTile } from '../../../Store/tileSelectionSlice';
import { useDispatch, useSelector } from 'react-redux';
import { FaXmark } from 'react-icons/fa6';
import { useGetTilesQuery, useSubmitTilesMutation } from '../../../api/tileSelection';
import LoadingSpiner, { SPINER_SIZES } from '../../Loader/LoadingSpiner';
import { useParams } from 'react-router-dom';
import concertS3UrlToHttps from '../../../utils/convertS3UrlToHttps';

// Helper function to parse coordinates from tile URL
function parseTileCoords(url: string) {
    const match = url.match(/\/16\/(\d+)_(\d+)\.jpg$/);
    if (match) {
        return `${match[1]}_${match[2]}`;
    }
    return null;
}

// Toast component
const Toast = ({ message, onClose, type = 'warning' }: { message: string, onClose: () => void, type?: 'warning' | 'success' }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const bgColor = type === 'warning' ? 'bg-amber-500' : 'bg-green-500';

    return (
        <div className={`fixed top-4 right-4 ${bgColor} text-white px-4 py-2 rounded shadow-lg z-50 animate-fadeIn`}>
            {message}
            <button onClick={onClose} className="ml-2 text-white">×</button>
        </div>
    );
};

export default function TileSelector() {
    const selectedTiles = useSelector(selectSelectedTiles);
    const dispatch = useDispatch();
    const { imageId, resultId } = useParams();
    const { data: previouslySubmittedTiles, isLoading: tilesLoading } = useGetTilesQuery(
        { imageId: imageId!, resultId: resultId! },
        { skip: !imageId || !resultId }
    );
    const [submitTiles, { isLoading }] = useSubmitTilesMutation();
    
    // State for toast
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'warning' | 'success'>('warning');
    
    // Ref for flashing tile
    const [flashingTileIndex, setFlashingTileIndex] = useState<number | null>(null);
    
    // Track the last selected tile URL to detect duplicates in current selection
    const [lastSelectedTileUrl, setLastSelectedTileUrl] = useState<string | null>(null);

    // Add this state variable for previously submitted tiles
    const [flashingPreviousTileIndex, setFlashingPreviousTileIndex] = useState<number | null>(null);

    // Dismiss toast when a new valid tile is selected
    useEffect(() => {
        if (selectedTiles.length === 0) return;
        
        // Get the most recently added tile
        const currentTile = selectedTiles[selectedTiles.length - 1];
        const currentTileUrl = currentTile.url;
        
        // Skip if we've already processed this tile
        if (currentTileUrl === lastSelectedTileUrl) return;
        setLastSelectedTileUrl(currentTileUrl);
        
        // Extract coordinates from the current tile URL
        const currentCoord = parseTileCoords(currentTileUrl);
        if (!currentCoord) return;
        
        // First check if it's a duplicate in current selection
        const duplicatesInSelection = selectedTiles
            .filter(tile => tile.url !== currentTileUrl) // Exclude the current tile
            .filter(tile => {
                const tileCoord = parseTileCoords(tile.url);
                return tileCoord === currentCoord;
            });
        
        if (duplicatesInSelection.length > 0) {
            // It's a duplicate in the current selection
            setToastMessage('Tile already selected for submission.');
            setToastType('warning');
            setShowToast(true);
            
            // Find the index of the first duplicate
            const existingTileIndex = selectedTiles.findIndex(tile => {
                if (tile.url === currentTileUrl) return false; // Skip the current tile
                const tileCoord = parseTileCoords(tile.url);
                return tileCoord === currentCoord;
            });
            
            if (existingTileIndex !== -1) {
                // Flash the existing tile
                setFlashingTileIndex(existingTileIndex);
                setTimeout(() => setFlashingTileIndex(null), 2000);
            }
            
            // Remove the duplicate
            dispatch(removeTile(currentTileUrl));
            return;
        }
        
        // Then check if it's in previously submitted tiles
        if (previouslySubmittedTiles?.length) {
            const matchingTileIndex = previouslySubmittedTiles.findIndex(tile => {
                const prevCoord = parseTileCoords(tile.source_tile_url);
                return prevCoord === currentCoord;
            });
            
            if (matchingTileIndex !== -1) {
                // Show warning for previously submitted tile
                setToastMessage('This tile has already been submitted!');
                setToastType('warning');
                setShowToast(true);
                
                // Flash the matching tile in the previously submitted list
                setFlashingPreviousTileIndex(matchingTileIndex);
                setTimeout(() => setFlashingPreviousTileIndex(null), 2000);
                
                // Remove the duplicate tile
                dispatch(removeTile(currentTileUrl));
                return;
            }
        }
        
        // If we got here, it's a valid new tile
        setToastMessage('Tile added to the list for submission.');
        setToastType('success');
        setShowToast(true);
        
    }, [selectedTiles, previouslySubmittedTiles, dispatch, lastSelectedTileUrl]);

    const handleSubmit = async () => {
        try {
            const urls = selectedTiles.map(({url, resultId}:TilePayload) => {
                return url
            })

            // unwrap so a failed submit throws instead of silently clearing the selection
            await submitTiles({
                sourceTileUrls: urls,
                resultId: selectedTiles[0].resultId
            }).unwrap();
            dispatch(clearSelection());
        } catch (error) {
            console.error('Error submitting tiles:', error);
            // the selection survives a failure now, but say so — otherwise the
            // button just returns to idle and the submit looks like it worked
            setToastMessage('Could not submit tiles. Your selection has been kept — try again.');
            setToastType('warning');
            setShowToast(true);
        }
    };

    return <div className='col-span-2 bg-neutral-200 dark:bg-neutral-800 border-r border-r-neutral-300 dark:border-r-neutral-600 overflow-y-auto'>
        {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} type={toastType} />}
        <div className="p-4">
            <h3 className="text-lg font-semibold mb-4">{selectedTiles.length ? `Selected Tiles` : "Tile Selection Mode"}{isLoading ? <LoadingSpiner className="mt-3" size={SPINER_SIZES.small}/> : ""}</h3>
            <p className="text-sm">{!selectedTiles.length && "Select tiles to submit for training by zooming down and then click on the area you want to select."}</p>
            <div className="grid grid-row-2 grid-cols-2 gap-2" style={{ maxHeight: "500px" }}>
                {selectedTiles.map((tile, index) => (
                    <div className='relative' key={`selected-${index}`}>
                        <button className="text-xs w-5 h-5 absolute z-60 p-1 color-white bg-slate-600 hover:bg-red-600 opacity-90 rounded top-1 left-1"
                            onClick={() => {
                                dispatch(removeTile(tile.url))
                            }}><FaXmark /></button>
                        <img
                            src={tile.url}
                            alt={`Selected tile ${index + 1}`}
                            className="w-32 h-30 object-cover rounded" />
                    </div>
                ))}
                {previouslySubmittedTiles?.length && <div className='col-span-2'><h3 className="text-lg font-semibold mb-4">Previously Selected Tiles</h3></div>}
                {previouslySubmittedTiles && previouslySubmittedTiles.map((tile: TileResponse, index: number) => (
                    <div 
                        className={`relative ${flashingPreviousTileIndex === index ? 'animate-pulse border-2 border-amber-500' : ''}`} 
                        key={`previous-${index}`}
                    >
                        <img
                            src={concertS3UrlToHttps(tile.source_tile_url)}
                            alt={`Selected tile ${index + 1}`}
                            className="w-32 h-30 object-cover rounded" />
                    </div>))}
                {selectedTiles.length ?
                    <div className="col-span-2">
                        <button className="mt-4 p-2 w-full bg-neutral-700 hover:bg-neutral-800 text-white text-sm rounded" onClick={handleSubmit}>SUBMIT</button>
                        <button className="mt-2 mb-6 p-1 w-full text-white text-sm rounded" onClick={()=>dispatch(clearSelection())}>CLEAR</button>
                    </div> : null}
            </div>
        </div>
    </div>
}